import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Loader2, Mail, BookOpen, CreditCard, GraduationCap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('');
  const [studentData, setStudentData] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API}/api/auth/verify-email/${token}`);
        const data = await response.json();
        
        if (response.ok) {
          if (data.already_verified) {
            setStatus('already_verified');
            setMessage('Your email has already been verified.');
          } else {
            setStatus('success');
            setMessage('Your email has been verified successfully!');
          }
          setStudentData(data);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed. Please try again.');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification. Please try again.');
      }
    };

    if (token) {
      verifyEmail();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 flex items-center justify-center px-4 py-12">
      <Card className="max-w-lg w-full p-8 text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-slate-800 mb-4">Verifying Your Email...</h1>
            <p className="text-slate-600">Please wait while we verify your email address.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-green-700 mb-4">Email Verified!</h1>
            <p className="text-slate-600 mb-6">
              Welcome, <strong>{studentData?.first_name}</strong>! Your account is now active.
            </p>
            
            {/* Next Steps */}
            <div className="bg-blue-50 rounded-xl p-6 mb-8 text-left">
              <h2 className="text-lg font-semibold text-blue-800 mb-4">Your Next Steps:</h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 flex items-center">
                      <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                      Select Your Course Package
                    </h3>
                    <p className="text-sm text-slate-600">Foundation (£75) or Pro (£215)</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-blue-600" />
                      Select Course Start Date
                    </h3>
                    <p className="text-sm text-slate-600">Choose from available dates</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 flex items-center">
                      <CreditCard className="w-4 h-4 mr-2 text-blue-600" />
                      Complete Payment
                    </h3>
                    <p className="text-sm text-slate-600">Secure checkout via Revolut</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-700 flex items-center">
                      <GraduationCap className="w-4 h-4 mr-2 text-green-600" />
                      Access Student Portal
                    </h3>
                    <p className="text-sm text-slate-600">Start your learning journey!</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/student-login')}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3"
              >
                Login & Select Course
              </Button>
              <Link to="/" className="block text-sm text-slate-500 hover:text-slate-700">
                Return to Homepage
              </Link>
            </div>
          </>
        )}

        {status === 'already_verified' && (
          <>
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-blue-700 mb-4">Already Verified</h1>
            <p className="text-slate-600 mb-6">
              Your email has already been verified. You can login to continue.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/student-login')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Login to Your Account
              </Button>
              <Link to="/" className="block text-sm text-slate-500 hover:text-slate-700">
                Return to Homepage
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-red-700 mb-4">Verification Failed</h1>
            <p className="text-slate-600 mb-6">{message}</p>
            
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/enroll')}
                variant="outline"
                className="w-full"
              >
                Try Enrolling Again
              </Button>
              <Link to="/support" className="block text-sm text-blue-600 hover:text-blue-700">
                Contact Support
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default VerifyEmail;
